import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { useRequestPayCardAssistance } from '../hooks/useRequestPayCardAssistance';

/**
 * *"Do you want to link a new pay card?"* — the returning candidate's ask, in front of the scan screen
 * [KPC-1a scenario *Optional for a returning candidate — ask, don't show*; decision 0077 §3; Figma
 * `Returning user / 07 · R1 · Pay card — link a new one? (ask)` — Desktop · iPad · Mobile, resolved by NAME].
 *
 * **Reached only from the arrival menu's pay-card row.** A returning candidate who picks *Search for a job*
 * reaches the pay card as the tail of that application and takes the new candidate's path, with no ask —
 * the prototype draws none there, and 0177's condition is the arrival REASON.
 *
 * **Two actions, and Back is the "no".** The prototype's own note on `#paycardAsk`: *"Not a Yes/No — 'No'
 * used to mean 'yes, scan the one I have', which reads backwards."* So there is no third button: a candidate
 * who does not need a card leaves through Back, back to the menu, having seen no scan screen and raised
 * nothing. ⛔ Do not add a *No* / *skip* / *continue* control here.
 *
 * Its own screen rather than a state of `PayCardScreen`: the scan screen is unchanged behind *Link a new pay
 * card*, the fidelity gate can seed this screen by name, and Back from the scan still lands on the menu
 * exactly as it did [`usePayCardErrand`].
 *
 * ⛔ This is the *do you need a card* question. The *do you still have the card on file* question is
 * KPC-1b's [0182], still draft, and is not built here.
 */
export function PayCardAskScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  /*
   * `arrival` is set on the only route here, so `useBack` takes its menu branch: back to *What brings you in
   * today?* [0177], and nothing is queued.
   */
  const back = useBack();
  const requestAssistance = useRequestPayCardAssistance();

  return (
    <KioskScreen>
      <KioskCard
        /*
          The returning frames draw no rail and no footer: the two actions sit in the content column.
          Errand rhythm — Logo 92→203, Header 203→251, Content 251 at 1440; flush at 375 too.
        */
        header={
          <>
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
        rhythm="flush"
      >
        {/*
          `Content`: pad T32 B32, the `Heading` block (28 + 8 + 40), 24 to `Ask` (19 + 4 + 17), 24 to
          `Ask actions` — two 52-tall full-width buttons 12 apart. Same at all three widths.
        */}
        <div className="flex w-full flex-col items-start gap-6 py-8">
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-[var(--foreground)]"
              ref={heading}
              tabIndex={-1}
            >
              {t('payCard.heading')}
            </h1>
            {/* The frame repeats the scan screen's subtitle verbatim, so it is the same key. */}
            <p className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
              {t('payCard.intro')}
            </p>
          </div>

          <div className="flex w-full flex-col items-start gap-1">
            <p className="w-full text-base font-semibold leading-[19px] text-[var(--foreground)]">
              {t('payCardAsk.question')}
            </p>
            <p className="w-full text-sm leading-[17px] text-[var(--muted-foreground)]">
              {t('payCardAsk.helper')}
            </p>
          </div>

          <div className="flex w-full flex-col gap-3">
            <Button
              fullWidth
              onClick={() => {
                goTo(SCREEN.payCard);
              }}
            >
              {t('payCardAsk.linkNew')}
            </Button>
            {/* The frame's brand stroke and brand label — `outline`, not the neutral secondary. */}
            <Button fullWidth onClick={requestAssistance} variant="outline">
              {t('payCard.needHelp')}
            </Button>
          </div>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
